import React, { useMemo } from 'react'
import { LDProvider } from 'launchdarkly-react-client-sdk'
import { LDContext, LDFlagSet, LDUser } from 'launchdarkly-js-sdk-common'

import config from 'config'

declare global {
  interface Window {
    __FLAGS_STATE__: LDFlagSet | undefined
  }
}

type Props = {
  children: React.ReactNode
  restaurantGuid: string
  managementGroupGuid?: string
}

/**
 * Wraps children with LaunchDarkly provider for feature flags.
 *
 * Uses <LDProvider> component directly to avoid HOC pattern that was
 * creating new component types on every render and causing remounting.
 */
export const LDProviderWrapper = ({
  children,
  restaurantGuid,
  managementGroupGuid
}: Props) => {
  // FIXME WOO-4180: These bootstrapFlags were not necessarily evaluated for the specific restaurant, but
  //  rather for the top-level, depending which page was rendered during SSR. This may result in flags
  //  changing once the restaurant level values are loaded client-side.
  const bootstrapFlags = useMemo(
    () => (typeof window !== 'undefined' ? window.__FLAGS_STATE__ : undefined),
    []
  )

  const ldContext: LDContext = useMemo(() => {
    return {
      key: restaurantGuid,
      custom: {
        // Allow attribute-based targeting on mgmt_group like we have elsewhere at Toast
        ...(managementGroupGuid ? { mgmt_group: managementGroupGuid } : {}) // Should always be set
      }
    } as LDUser
  }, [restaurantGuid, managementGroupGuid])

  const ldOptions = useMemo(
    () => ({
      bootstrap: bootstrapFlags,
      sendEvents: false
    }),
    [bootstrapFlags]
  )

  return (
    <LDProvider
      clientSideID={config.ldClientId}
      context={ldContext}
      options={ldOptions}
    >
      {children}
    </LDProvider>
  )
}
